'use client';

import Image from 'next/image';
import Link from '~/(tenant)/_components/tenant-link';
import { useEffect, useId, useRef, useState, type CSSProperties, type ReactNode } from 'react';

import { ArrowUpRight, ChevronDown, Menu, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import type { LandingNav, LandingNavLink, LandingTheme } from '../../_lib/schema/landing-config.schema';
import { pickLocalized } from './landing-context';
import { contrastForeground, landingTypographyStyle } from './landing-design';
import './landing.module.css';
import styles from './sports-site-chrome.module.css';

type SportsChromeProps = {
  nav: LandingNav;
  businessName?: string | null;
  logoUrl?: string | null;
  locale?: string;
  disableNavigation?: boolean;
  fontFamily?: LandingTheme['fontFamily'];
};

function ChromeLink({ href, children, className, disabled, onClick }: {
  href: string;
  children: ReactNode;
  className?: string;
  disabled?: boolean;
  onClick?: () => void;
}) {
  return (
    <Link href={href} prefetch={disabled ? false : undefined} className={className} aria-disabled={disabled || undefined} onClick={(event) => {
      if (disabled) event.preventDefault();
      onClick?.();
    }}>
      {children}
    </Link>
  );
}

function Brand({ nav, logoUrl, businessName, disableNavigation }: SportsChromeProps) {
  return (
    <ChromeLink href="/" className={styles.brand} disabled={disableNavigation}>
      {logoUrl ? (
        <Image src={logoUrl} alt={nav.logo?.alt || businessName || ''} width={220} height={72} unoptimized className={styles.logo} />
      ) : <span>{businessName}</span>}
    </ChromeLink>
  );
}

export function SportsSiteHeader({ nav, businessName, logoUrl, locale, disableNavigation, accountControl, languageControl, memberNavigation, accent = '#2563eb', accentForeground, fontFamily }: SportsChromeProps & {
  accountControl?: ReactNode;
  languageControl?: ReactNode;
  memberNavigation?: ReactNode;
  accent?: string;
  accentForeground?: string;
}) {
  const { t, i18n } = useTranslation();
  const language = locale ?? i18n.language;
  const id = useId();
  const header = useRef<HTMLElement>(null);
  const [state, setState] = useState({ menuOpen: false, group: -1 });
  const label = (link: LandingNavLink) => pickLocalized(link.label, link.label_ar, language) ?? link.label;
  const close = () => setState({ menuOpen: false, group: -1 });
  const announcement = nav.announcement ? pickLocalized(nav.announcement.text, nav.announcement.text_ar, language)?.trim() : undefined;

  useEffect(() => {
    const element = header.current;
    const container = element?.parentElement;
    if (!element || !container || typeof ResizeObserver === 'undefined') return;
    const previous = container.style.getPropertyValue('--site-header-h');
    const measure = () => container.style.setProperty('--site-header-h', `${element.getBoundingClientRect().height}px`);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => {
      observer.disconnect();
      if (previous) container.style.setProperty('--site-header-h', previous);
      else container.style.removeProperty('--site-header-h');
    };
  }, []);

  return (
    <>
    <header ref={header} className={styles.header} data-font-family={fontFamily} data-link-case={nav.linkCase} style={{ ...landingTypographyStyle(fontFamily), '--sports-accent': accent, '--sports-accent-foreground': accentForeground ?? contrastForeground(accent) } as CSSProperties} data-test="sports-site-header" onKeyDown={(event) => {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      const target = state.group >= 0 ? `group-${state.group}` : 'menu';
      header.current?.querySelector<HTMLButtonElement>(`[data-sports-trigger="${target}"]`)?.focus();
      setState(state.group >= 0 ? { ...state, group: -1 } : { menuOpen: false, group: -1 });
    }} onBlur={(event) => {
      if (!event.currentTarget.contains(event.relatedTarget)) close();
    }}>
      <div className={styles.headerRow}>
        <Brand nav={nav} logoUrl={logoUrl} businessName={businessName} disableNavigation={disableNavigation} />
        <button type="button" className={styles.menuToggle} data-sports-trigger="menu" aria-expanded={state.menuOpen} aria-controls={`${id}-navigation`} aria-label={t('marketing:sportsChrome.menu')} onClick={() => setState({ menuOpen: !state.menuOpen, group: -1 })}>
          {state.menuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
        <nav id={`${id}-navigation`} className={styles.navigation} data-open={state.menuOpen} aria-label={t('marketing:sportsChrome.navigation')}>
          {nav.links.map((link, index) => (
            <div className={styles.navItem} key={`${link.href}-${index}`}>
              {link.children?.length ? (
                <>
                  <button type="button" className={styles.groupTrigger} data-sports-trigger={`group-${index}`} aria-expanded={state.group === index} aria-controls={`${id}-group-${index}`} onClick={() => setState({ ...state, group: state.group === index ? -1 : index })}>
                    {label(link)} <ChevronDown size={14} aria-hidden="true" />
                  </button>
                  <div id={`${id}-group-${index}`} className={styles.dropPanel} hidden={state.group !== index}>
                    <ChromeLink href={link.href} disabled={disableNavigation} onClick={close} className={styles.groupOverview}>
                      {label(link)} <ArrowUpRight size={24} aria-hidden="true" />
                    </ChromeLink>
                    <div className={styles.childLinks}>
                      {link.children.map((child, childIndex) => <ChromeLink key={`${child.href}-${childIndex}`} href={child.href} disabled={disableNavigation} onClick={close}>{label(child)} <ArrowUpRight size={16} aria-hidden="true" /></ChromeLink>)}
                    </div>
                  </div>
                </>
              ) : <ChromeLink href={link.href} className={styles.navLink} disabled={disableNavigation} onClick={close}>{label(link)}</ChromeLink>}
            </div>
          ))}
          {nav.primaryAction ? <ChromeLink href={nav.primaryAction.href} className={`${styles.primaryAction} ${styles.mobilePrimaryAction}`} disabled={disableNavigation} onClick={close}>{label(nav.primaryAction)} <ArrowUpRight size={16} aria-hidden="true" /></ChromeLink> : null}
        </nav>
        <div className={styles.actions}>
          {languageControl}
          {accountControl}
          {nav.primaryAction ? <ChromeLink href={nav.primaryAction.href} className={styles.primaryAction} disabled={disableNavigation} onClick={close}>{label(nav.primaryAction)} <ArrowUpRight size={16} aria-hidden="true" /></ChromeLink> : null}
        </div>
      </div>
      {memberNavigation}
    </header>
    {/* Outside the sticky header so it scrolls away with the page. */}
    {announcement ? (
      <div className={styles.announcement} style={{ '--sports-accent': accent, '--sports-accent-foreground': accentForeground ?? contrastForeground(accent) } as CSSProperties} data-test="sports-site-announcement">
        {nav.announcement?.href
          ? <ChromeLink href={nav.announcement.href} disabled={disableNavigation}>{announcement}</ChromeLink>
          : <p>{announcement}</p>}
      </div>
    ) : null}
    </>
  );
}

export function SportsSiteFooter({ nav, businessName, logoUrl, locale, disableNavigation, contact, legalControls, fontFamily }: SportsChromeProps & {
  contact?: { address?: string | null; city?: string | null; country?: string | null; phone?: string | null; email?: string | null } | null;
  legalControls?: ReactNode;
}) {
  const { t, i18n } = useTranslation();
  const language = locale ?? i18n.language;
  const cityCountry = [contact?.city, contact?.country].filter(Boolean).join(', ');
  const label = (link: LandingNavLink) => pickLocalized(link.label, link.label_ar, language) ?? link.label;

  return (
    <footer className={styles.footer} style={landingTypographyStyle(fontFamily)} data-font-family={fontFamily} data-test="sports-site-footer">
      <div className={styles.footerInner}>
        <div className={styles.footerBrand}>
          <Brand nav={nav} logoUrl={logoUrl} businessName={businessName} disableNavigation={disableNavigation} />
          {contact?.address ? <p>{contact.address}</p> : null}
          {cityCountry ? <p>{cityCountry}</p> : null}
          {contact?.phone ? <ChromeLink href={`tel:${contact.phone}`} disabled={disableNavigation}>{contact.phone}</ChromeLink> : null}
          {contact?.email ? <ChromeLink href={`mailto:${contact.email}`} disabled={disableNavigation}>{contact.email}</ChromeLink> : null}
          <ChromeLink href="/contact" disabled={disableNavigation}>{t('marketing:footer.contactUs')} <ArrowUpRight size={16} aria-hidden="true" /></ChromeLink>
        </div>
        <nav className={styles.footerGroups} aria-label={t('marketing:sportsChrome.footerNavigation')}>
          {nav.links.filter((link) => link.children?.length).map((link, index) => <div className={styles.footerGroup} key={`${link.href}-${index}`}>
            <ChromeLink href={link.href} className={styles.footerHeading} disabled={disableNavigation}>{label(link)}</ChromeLink>
            {link.children?.map((child, childIndex) => <ChromeLink key={`${child.href}-${childIndex}`} href={child.href} disabled={disableNavigation}>{label(child)}</ChromeLink>)}
          </div>)}
          {/* Single links would each become a heading with nothing under it; group them in one column instead. */}
          {nav.links.some((link) => !link.children?.length) ? <div className={styles.footerGroup}>
            <span className={styles.footerHeading}>{t('marketing:sportsChrome.explore')}</span>
            {nav.links.filter((link) => !link.children?.length).map((link, index) => <ChromeLink key={`${link.href}-${index}`} href={link.href} disabled={disableNavigation}>{label(link)}</ChromeLink>)}
          </div> : null}
        </nav>
        <div className={styles.footerGroup}>
          <span className={styles.footerHeading}>{t('marketing:legal.label')}</span>
          <ChromeLink href="/privacy-policy" disabled={disableNavigation}>{t('marketing:privacyPolicy')}</ChromeLink>
          <ChromeLink href="/terms-of-service" disabled={disableNavigation}>{t('marketing:termsOfService')}</ChromeLink>
          <ChromeLink href="/cookie-policy" disabled={disableNavigation}>{t('marketing:cookiePolicy')}</ChromeLink>
          {legalControls}
        </div>
      </div>
      <div className={styles.footerBottom}>
        <span>{businessName ? t('marketing:copyright', { product: businessName, year: new Date().getFullYear() }) : null}</span>
        <span>{t('marketing:footer.poweredBy')}</span>
      </div>
    </footer>
  );
}
